import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { useAuth } from "../AuthContext";
import { PageHeader, EmptyState, Modal, Field, Button, inr } from "../components/UI";
import { Package, Plus, Pencil, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

const categories = [
  { v: "poster", l: "Poster" },
  { v: "leaflet", l: "Leaflet" },
  { v: "hospital_file", l: "Hospital File" },
  { v: "letter_pad", l: "Letter Pad" },
  { v: "visiting_card", l: "Visiting Card" },
  { v: "other", l: "Other" },
];

const blank = { name: "", category: "poster", unit: "pcs", default_price: 0, cost_price: 0, description: "", incentive_pct: 0 };

export default function Products() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    setLoading(true);
    try { setList((await api.get("/products")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, default_price: Number(form.default_price), cost_price: Number(form.cost_price), incentive_pct: Number(form.incentive_pct || 0) };
      if (editing) { await api.put(`/products/${editing.id}`, payload); toast.success("Product updated"); }
      else { await api.post("/products", payload); toast.success("Product added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl">
      <PageHeader
        title="Product Catalog"
        subtitle="Posters, leaflets, hospital files, letter pads, visiting cards & more"
        action={<Button onClick={() => { setEditing(null); setForm(blank); setOpen(true); }} data-testid="add-product-btn"><Plus size={16}/>Add Product</Button>}
      />
      {loading ? (
        <div className="text-[#475569]">Loading…</div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={Package}
          title="No products yet"
          hint="Add the items you print so you can quickly select them when creating orders or invoices."
          action={<Button onClick={() => setOpen(true)}><Plus size={16}/> Add product</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-grid">
          {list.map((p) => (
            <div key={p.id} className="card-flat p-5" data-testid={`product-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#C1121F] font-semibold mb-1">{categories.find(c => c.v === p.category)?.l || p.category}</div>
                  <h3 className="font-display font-bold text-lg">{p.name}</h3>
                  {p.description && <div className="text-sm text-[#475569] mt-1 line-clamp-2">{p.description}</div>}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(p); setForm({...p}); setOpen(true); }} data-testid={`edit-product-${p.id}`} className="p-1.5 text-[#475569] hover:text-[#C1121F]"><Pencil size={15}/></button>
                  <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} className="p-1.5 text-[#475569] hover:text-red-600"><Trash2 size={15}/></button>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">Price / {p.unit}</div>
                    <div className="font-mono-num font-bold text-lg mt-0.5">{inr(p.default_price)}</div>
                  </div>
                  {isAdmin && (
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-[#94A3B8] flex items-center justify-end gap-1"><Lock size={9}/> Cost</div>
                      <div className="font-mono-num font-bold text-lg mt-0.5 text-[#475569]">{p.cost_price > 0 ? inr(p.cost_price) : "—"}</div>
                    </div>
                  )}
                </div>
                {isAdmin && p.cost_price > 0 && p.default_price > 0 && (
                  <div className="mt-2 text-[10px]">
                    <span className={`font-semibold ${((p.default_price - p.cost_price) / p.default_price * 100) > 30 ? "text-green-700" : "text-amber-600"}`}>
                      Margin: {((p.default_price - p.cost_price) / p.default_price * 100).toFixed(1)}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Product" : "Add Product"}>
        <form onSubmit={save} className="space-y-4">
          <Field label="Name" required>
            <input data-testid="prod-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="input-base" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category" required>
              <select data-testid="prod-category" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="input-base">
                {categories.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
            <Field label="Unit">
              <input data-testid="prod-unit" value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} className="input-base" placeholder="pcs / kg / set" />
            </Field>
          </div>
          <Field label="Default Price (₹)" required>
            <input data-testid="prod-price" type="number" step="0.01" required value={form.default_price} onChange={(e) => setForm({...form, default_price: e.target.value})} className="input-base" />
          </Field>
          {isAdmin && (
            <Field label="Cost Price (₹) — admin only">
              <input data-testid="prod-cost" type="number" step="0.01" value={form.cost_price} onChange={(e) => setForm({...form, cost_price: e.target.value})} className="input-base" placeholder="Internal cost per unit" />
            </Field>
          )}
          {isAdmin && (
            <Field label="Sales Incentive % (per-product bonus, optional)">
              <input data-testid="prod-incentive" type="number" step="0.1" value={form.incentive_pct ?? 0} onChange={(e) => setForm({...form, incentive_pct: e.target.value})} className="input-base font-mono-num w-32" placeholder="0" />
              <div className="text-[10px] text-[#94A3B8] mt-1">Extra % paid to sales staff on this product's revenue (added on top of base slab rule).</div>
            </Field>
          )}
          <Field label="Description">
            <textarea data-testid="prod-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="input-base" rows={2} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="save-product-btn">{editing ? "Save" : "Add Product"}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
